/**
 * ProductTypeAliasSection - Map Shopify product types to display names
 *
 * Allows organizations to define aliases for Shopify product types
 * shown in the picklist. Example: "Custom Tee" → "Classic Tee".
 *
 * Self-contained component that manages its own data fetching.
 *
 * Design: Touch-friendly (44px+ targets) for warehouse tablets.
 */
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Loader2,
  Save,
  Plus,
  X,
  ArrowRight,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

interface ProductTypeAliasSectionProps {
  embedded?: boolean;
}

export function ProductTypeAliasSection({ embedded = false }: ProductTypeAliasSectionProps) {
  const { t } = useTranslation('settings');
  const [aliases, setAliases] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // New alias form state
  const [newSourceType, setNewSourceType] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');

  // Queries
  const aliasQuery = trpc.productionSettings.getProductTypeAliases.useQuery();
  const productTypesQuery = trpc.productionSettings.getDistinctProductTypes.useQuery();

  // Mutation
  const updateMutation = trpc.productionSettings.updateProductTypeAliases.useMutation({
    onSuccess: () => {
      toast.success(t('production.productTypeAliases.saved'));
      setHasChanges(false);
      aliasQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to save: ${error.message}`);
    },
  });

  // Sync local state with server data
  useEffect(() => {
    if (aliasQuery.data) {
      setAliases(aliasQuery.data.aliases);
      setHasChanges(false);
    }
  }, [aliasQuery.data]);

  // Available product types (excluding already aliased ones)
  const availableTypes = useMemo(() => {
    const allTypes = productTypesQuery.data?.productTypes ?? [];
    return allTypes.filter(t => !(t in aliases));
  }, [productTypesQuery.data, aliases]);

  const handleAddAlias = () => {
    if (!newSourceType.trim() || !newDisplayName.trim()) return;
    if (newSourceType in aliases) {
      toast.error(`"${newSourceType}" already has an alias`);
      return;
    }

    setAliases(prev => ({ ...prev, [newSourceType]: newDisplayName.trim() }));
    setNewSourceType('');
    setNewDisplayName('');
    setHasChanges(true);
  };

  const handleRemoveAlias = (key: string) => {
    setAliases(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate({ aliases });
  };

  const aliasEntries = Object.entries(aliases);
  const isLoading = aliasQuery.isLoading || productTypesQuery.isLoading;

  const content = (
    <div className="space-y-4">
      {/* Description */}
      <p className="text-sm text-muted-foreground">
        Remap Shopify product types to custom display names in the picklist.
        The original data is preserved. Aliases only affect how types appear.
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Existing aliases table */}
          {aliasEntries.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-base">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium">Shopify Type</th>
                    <th className="text-left px-2 py-3 font-medium w-8"></th>
                    <th className="text-left px-4 py-3 font-medium">Display As</th>
                    <th className="w-14"></th>
                  </tr>
                </thead>
                <tbody>
                  {aliasEntries.map(([source, display]) => (
                    <tr key={source} className="border-b last:border-b-0">
                      <td className="px-4 py-3 font-mono text-sm">{source}</td>
                      <td className="px-2 py-3 text-muted-foreground">
                        <ArrowRight className="h-4 w-4" />
                      </td>
                      <td className="px-4 py-3">{display}</td>
                      <td className="px-2 py-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11"
                          onClick={() => handleRemoveAlias(source)}
                          aria-label={`Remove alias for ${source}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add new alias form */}
          <div className="flex flex-col sm:flex-row gap-2 items-end">
            <div className="flex-1 space-y-1">
              <Label htmlFor="new-source-type" className="text-sm text-muted-foreground">
                Shopify Type
              </Label>
              {availableTypes.length > 0 ? (
                <Select value={newSourceType} onValueChange={setNewSourceType}>
                  <SelectTrigger id="new-source-type" className="h-11">
                    <SelectValue placeholder="Select product type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="new-source-type"
                  value={newSourceType}
                  onChange={(e) => setNewSourceType(e.target.value)}
                  placeholder="Product type name..."
                  className="h-11"
                />
              )}
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 hidden sm:block mb-3" />
            <div className="flex-1 space-y-1">
              <Label htmlFor="new-display-name" className="text-sm text-muted-foreground">
                Display As
              </Label>
              <Input
                id="new-display-name"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder="Custom display name..."
                className="h-11"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddAlias();
                  }
                }}
              />
            </div>
            <Button
              variant="outline"
              className="h-11"
              onClick={handleAddAlias}
              disabled={!newSourceType.trim() || !newDisplayName.trim()}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>

          {/* Empty state */}
          {aliasEntries.length === 0 && (
            <p className="text-sm text-muted-foreground/70 text-center py-2">
              No aliases configured. Product types will display as synced from Shopify.
            </p>
          )}

          {/* Save button */}
          {hasChanges && (
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="h-12 gap-2"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {t('production.common.saveChanges')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="border rounded-lg p-4">
      <h3 className="font-medium mb-3">Product Type Aliases</h3>
      {content}
    </div>
  );
}
